<template>
  <div class="container-tight py-4">
    <div class="card card-md">
      <div class="text-center mt-4">
        <a href="." class="navbar-brand navbar-brand-autodark">
          <img :src="logoUrl" :style="{ height: logoHeight + 'px' }" alt="" class="logo-image" />
        </a>
      </div>
      <div class="card-body text-center py-4 p-sm-5">
        <h1 class="">{{ headerText }}</h1>
        <p class="text-muted">
          {{ instructionText }}
        </p>

        <button v-if="isPinSet && isBiometricSupported"
                @click="handleBiometricAuth"
                class="btn btn-primary mb-4">
          {{ __DICT__.useBiometric }}
        </button>

        <div class="mb-4">
          <span v-for="i in 4" :key="i" class="pin-dot mx-1">
            {{ pin.length >= i ? '●' : '○' }}
          </span>
        </div>

        <div class="mb-4">
          <div class="row g-2">
            <div v-for="n in 9" :key="n" class="col-4">
              <button class="btn btn-light w-100 py-3"
                      @click="addDigit(n)"
                      :disabled="isLocked">
                {{ n }}
              </button>
            </div>
            <div class="col-4">
              <button class="btn btn-light w-100 py-3"
                      @click="removeDigit"
                      :disabled="isLocked">
                <IconBackspace class="icon" :size="24" />
              </button>
            </div>
            <div class="col-4">
              <button class="btn btn-light w-100 py-3"
                      @click="addDigit(0)"
                      :disabled="isLocked">
                0
              </button>
            </div>
            <div class="col-4">
              <button class="btn btn-primary w-100 py-3"
                      @click="handleSubmit"
                      :disabled="isLocked || pin.length !== 4">
                {{ submitButtonText }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="error" class="alert alert-danger" role="alert">
          {{ error }}
        </div>

        <div v-if="isLocked" class="alert alert-warning" role="alert">
          {{ __DICT__.accountLocked }}
        </div>

        <div v-else-if="attemptsRemaining < 3 && isPinSet"
             class="alert alert-warning" role="alert">
          {{ __DICT__.attemptsRemaining.replace('{n}', attemptsRemaining) }}
        </div>
      </div>
    </div>
    <div @click="handleLogout" class="btn btn-sm btn-ghost-primary mt-4 mb-2 w-100">{{ __DICT__.logOut }}</div>

  </div>
</template>

<style scoped>
.pin-dot {
  font-size: 24px;
  color: var(--tblr-primary);
}
</style>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { IconBackspace } from '@tabler/icons-vue'
import { useAuthStore } from '@/stores/authStore'
import squareLogo from '@brand/assets/square.png'
import { useRouter } from 'vue-router'
import { useBottomNavStore } from '@/stores/bottomNavStore'
import brandConfig from '@brand/config.js'
import {useWeekStore} from "@/stores/week.js";

const authStore = useAuthStore()
const weekStore = useWeekStore()

const router = useRouter()
const bottomNavStore = useBottomNavStore()
const logoHeight = ref(50)
const logoUrl = squareLogo

const pin = ref('')
const confirmPin = ref('')
const error = ref('')
const isPinSet = ref(false)
const isBiometricSupported = ref(false)
const isConfirming = ref(false)

// Computed properties for UI states
const isLocked = computed(() => authStore.isLocked)
const attemptsRemaining = computed(() => Math.max(0, 5 - authStore.attemptCount))

const handleLogout = () => {
  weekStore.isLoading = false;
  weekStore.error = null;
  authStore.logOut()
  router.push('/login')
}
const submitButtonText = computed(() => {
  if (isPinSet.value) return __DICT__.unlock
  return isConfirming.value ? __DICT__.confirm : __DICT__.setPin
})

const headerText = computed(() => {
  if (isPinSet.value) return __DICT__.unlockApp
  return isConfirming.value ? __DICT__.confirmPin : __DICT__.setPin
})

const instructionText = computed(() => {
  if (isPinSet.value) return __DICT__.unlockInstruction
  return isConfirming.value ? __DICT__.confirmPinInstruction : __DICT__.setPinInstruction
})

onMounted(async () => {
  // Hide bottom nav on this screen
  bottomNavStore.setBottomNavVisibility(false)

  isPinSet.value = await authStore.isPinSet()
  isBiometricSupported.value = await checkBiometricSupport()

  // Reset the unlocked state when mounting PinPad
  if (isPinSet.value) {
    authStore.setPwaUnlocked(false)
  }
})

const checkBiometricSupport = async () => {
  if (!window.PublicKeyCredential) return false
  return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
}

const clearError = () => {
  error.value = ''
}

const resetPinState = () => {
  pin.value = ''
  confirmPin.value = ''
  isConfirming.value = false
  clearError()
}

const addDigit = (digit) => {
  if (isLocked.value) return
  clearError()
  if (pin.value.length < 4) {
    pin.value += digit
  }
}

const removeDigit = () => {
  if (isLocked.value) return
  pin.value = pin.value.slice(0, -1)
  clearError()
}

const handleBiometricAuth = async () => {
  try {
    // Generate a random challenge
    const challenge = new Uint8Array(32);
    window.crypto.getRandomValues(challenge);

    const publicKeyCredentialCreationOptions = {
      challenge: challenge,
      rp: {
        name: brandConfig.name,
        id: window.location.hostname,
      },
      user: {
        id: new Uint8Array(16),
        name: "Rep",
        displayName: "Rep App",

      },
      pubKeyCredParams: [
        { type: "public-key", alg: -7 }, // ES256
        { type: "public-key", alg: -257 } // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: "platform",
        userVerification: "required"
      },
      timeout: 60000,
      attestation: "none"
    };

    const credential = await navigator.credentials.create({
      publicKey: publicKeyCredentialCreationOptions
    });

    // Here you would typically send the credential to your server for verification

    authStore.setPwaUnlocked(true);
    authStore.resetActivityTimer();
    router.push('/');
  } catch (err) {
    console.error('Fingerprint authentication failed:', err);
    error.value = __DICT__.fingerprintError || 'Fingerprint authentication failed';
  }
};

const validatePin = (pinToValidate) => {
  if (pinToValidate.length !== 4) {
    error.value = __DICT__.pinLengthError
    return false
  }

  // Check for sequential numbers
  const sequential = '01234567890'
  const reverseSequential = '98765432109'
  if (sequential.includes(pinToValidate) || reverseSequential.includes(pinToValidate)) {
    error.value = __DICT__.pinComplexityError
    return false
  }

  // Check for repeated numbers
  if (pinToValidate.split('').every(char => char === pinToValidate[0])) {
    error.value = __DICT__.pinComplexityError
    return false
  }

  return true
}

const handleSubmit = async () => {
  if (isLocked.value) return
  if (!validatePin(pin.value)) {
    pin.value = ''
    return
  }
  try {
    if (isPinSet.value) {
      // Handle unlock
      if (await authStore.verifyPwaPin(pin.value)) {
        authStore.setPwaUnlocked(true)
        authStore.resetActivityTimer()
        await router.push('/')
      } else {
        error.value = __DICT__.incorrectPin
        pin.value = ''
      }
    } else {
      // Handle PIN setup
      if (!isConfirming.value) {
        confirmPin.value = pin.value
        pin.value = ''
        isConfirming.value = true
        return
      }

      if (pin.value !== confirmPin.value) {
        error.value = __DICT__.pinMismatch
        resetPinState()
        return
      }

      await authStore.setPwaPin(pin.value)
      await router.push('/')
    }
  } catch (err) {
    console.error(err)
    error.value = __DICT__.genericError
    resetPinState()
  }
}
</script>